/** 行操作ボタン可否ルール — 判定式のSSOT。ロジックをここ以外に書くな。
 *
 * TERMINAL集合（AC0): 不可逆操作は fail-closed（状態不明="-"を含む）
 * クロージング/トスアップ取消 → TERMINAL に入ったら disabled
 * トスアップ複製             → TERMINAL に入ったら enabled（押せる）
 */
export const TERMINAL = new Set([
  "クロージング-成功",
  "クロージング-成功（未エントリー）",
  "クロージング-成功（他社）",
  "クロージング-失敗",
  "対象外",
  "条件期間外",
  "長期不出",
  "-",
]);

/** 判定に使うキーの正規化型（一覧側アダプタと詳細側アダプタが埋める） */
export interface TFFacts {
  /** toss_up_status (一覧側: toss_up_status_func / 詳細側: toss_up_status) */
  status?: string;
  /** apply_date (一覧側: apply_date_func / 詳細側: apply_date) */
  applyDate?: string;
  /** opening_date (一覧側: opening_date_func / 詳細側: opening_date) */
  openingDate?: string;
  /** cancell_date (一覧側: cancell_date_func / 詳細側: cancell_date) */
  cancellDate?: string;
  /** customer_phone_number (一覧側: customer_phone_number_func / 詳細側: customer_phone_number) */
  customerPhoneNumber?: string;
  /** cancell_action (一覧側 = 詳細側 同名。詳細側はB1でBE追加) */
  cancellAction?: string;
  /** contract_deactivation (一覧側 = 詳細側 同名。詳細側はB1でBE追加) */
  contractDeactivation?: number | null;
  /** toss_up_deactivation (一覧側 = 詳細側 同名。詳細側はB2でBE追加) */
  tossUpDeactivation?: number | null;
  /** closable_flg (一覧側 = 詳細側 同名。詳細側はB2でBE追加) */
  closableFlg?: number | string | null;
  /** login_status (account/get が返す) */
  loginStatus?: string;
  /** id */
  id?: string;
  /** no_entry_flg_pd — 一覧専用（取扱い停止・再開の可否判定） */
  noEntryFlgPd?: number | null;
  /** screen_column_system_default — 一覧専用（項目削除の可否判定） */
  screenColumnSystemDefault?: string;
}

/**
 * ボタン名・正規化facts・コンテキストからdisabled値を返す唯一の判定関数。
 * 未定義のボタン名は false（アクセシブル）を返す。
 */
export function isTableFunctionDisabled(
  name: string,
  facts: TFFacts,
  ctx: { pathname: string }
): boolean {
  switch (name) {
    case "クロージング":
      return (
        TERMINAL.has(facts.status ?? "-") ||
        facts.closableFlg === 0 ||
        facts.closableFlg === "0"
      );

    case "トスアップ複製":
      // /toss_up ページのみ判定（他ページでは常に有効）
      // TERMINAL に入ったら enabled（複製可能）、非終端は disabled
      return ctx.pathname === "/toss_up"
        ? (facts.status ?? "-") === "-" || !TERMINAL.has(facts.status ?? "-")
        : false;

    case "トスアップ取消":
      return (
        TERMINAL.has(facts.status ?? "-") ||
        facts.tossUpDeactivation === 1
      );

    case "エントリー取消":
      return (
        facts.openingDate !== "-" ||
        facts.contractDeactivation === 1
      );

    case "エントリー複製":
      return facts.contractDeactivation === 1;

    case "個人情報削除":
      return (
        facts.cancellDate === "-" ||
        facts.customerPhoneNumber === "削除済"
      );

    case "解約":
      return (
        facts.cancellAction !== "手動解約可能" ||
        facts.applyDate === "-" ||
        facts.cancellDate !== "-" ||
        facts.contractDeactivation === 1
      );

    case "パスワードリセット":
      // == 意図的（"0" == 0 のような変換を避けるため既存実装に合わせて ==）
      // eslint-disable-next-line eqeqeq
      return facts.loginStatus == "ログイン不可能";

    case "取扱い停止・再開":
      return facts.noEntryFlgPd === 1;

    case "項目削除":
      return facts.screenColumnSystemDefault === "デフォルト項目";

    default:
      return false;
  }
}

/** 一覧側アダプタ: row (dataDifine) → TFFacts。_func 別名を剥がす。 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function rowToFacts(row: Record<string, any>): TFFacts {
  return {
    status: row.toss_up_status_func as string,
    applyDate: row.apply_date_func as string,
    openingDate: row.opening_date_func as string,
    cancellDate: row.cancell_date_func as string,
    customerPhoneNumber: row.customer_phone_number_func as string,
    cancellAction: row.cancell_action as string,
    contractDeactivation: row.contract_deactivation as number | null,
    tossUpDeactivation: row.toss_up_deactivation as number | null,
    closableFlg: row.closable_flg as number | string | null,
    loginStatus: row.login_status as string,
    id: row.id as string,
    noEntryFlgPd: row.no_entry_flg_pd as number | null,
    screenColumnSystemDefault: row.screen_column_system_default as string,
  };
}

/** 詳細側アダプタ: dataDetail (Record<string, unknown>) → TFFacts。
 *  _func 別名はなく素のキー名をそのまま使う。 */
export function detailToFacts(dataDetail: Record<string, unknown>): TFFacts {
  return {
    status: dataDetail["toss_up_status"] as string,
    applyDate: dataDetail["apply_date"] as string,
    openingDate: dataDetail["opening_date"] as string,
    cancellDate: dataDetail["cancell_date"] as string,
    customerPhoneNumber: dataDetail["customer_phone_number"] as string,
    cancellAction: dataDetail["cancell_action"] as string,
    contractDeactivation: dataDetail["contract_deactivation"] as number | null,
    tossUpDeactivation: dataDetail["toss_up_deactivation"] as number | null,
    closableFlg: dataDetail["closable_flg"] as number | string | null,
    loginStatus: dataDetail["login_status"] as string,
    id: dataDetail["id"] as string,
    // noEntryFlgPd / screenColumnSystemDefault は詳細側では使わない
  };
}
